<template>
  <div class="home" :class="{ ready: pageReady }">
    <header class="site-header">
      <div class="header-inner">
        <router-link to="/home" class="brand" aria-label="xyo.chat 首页">
          <span>xyo</span><small>.chat</small>
        </router-link>

        <nav class="header-nav" aria-label="首页导航">
          <a href="#relationships">适合什么关系</a>
          <a href="#features">功能</a>
          <a href="#how">如何开始</a>
          <a href="#boundaries">边界</a>
        </nav>

        <div v-if="loggedIn" class="header-actions">
          <span class="user-name">{{ displayName }}</span>
          <router-link to="/chat" class="header-button">回到聊天</router-link>
          <button type="button" class="logout" @click="logout">退出</button>
        </div>
        <div v-else class="header-actions">
          <router-link to="/login" class="login">登录</router-link>
          <router-link to="/login" class="header-button">创建角色</router-link>
        </div>
      </div>
    </header>

    <main>
      <section ref="heroSection" class="hero" aria-labelledby="hero-title">
        <div ref="heroPin" class="hero-pin">
          <canvas ref="heroCanvas" class="hero-atmosphere" aria-hidden="true"></canvas>

          <div class="hero-night" aria-hidden="true">
            <div class="memory-ribbon"><i></i><i></i><i></i></div>
            <div class="ambient-vignette"></div>
            <div class="night-grain"></div>
          </div>

          <div class="presence-echo" aria-hidden="true">
            <span class="echo-word echo-word-one">称呼</span>
            <span class="echo-word echo-word-two">遗憾</span>
            <span class="echo-word echo-word-three">记得</span>
            <div class="echo-orbit"><i></i><i></i><b></b></div>
          </div>

          <div ref="heroIntro" class="hero-intro">
            <p class="eyebrow"><span></span>xyo.chat / CYBER EX · 赛博前任</p>
            <h1 id="hero-title">有些关系结束了，<br /><em>回声还留在这里。</em></h1>
            <p class="hero-lead">写下熟悉的性格、语气与共同经历，让未说完的话有一个新的去处。</p>

            <div class="hero-actions">
              <router-link :to="primaryRoute" class="primary-button">
                <span>{{ loggedIn ? "继续这段关系" : "创建我的赛博前任" }}</span>
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M5 12h13M14 7l5 5-5 5" />
                </svg>
              </router-link>
              <a href="#conversation" class="quiet-link">看看如何相处</a>
            </div>

            <p class="honest-note"><i></i>不是复活，也不冒充现实中的 TA。</p>
          </div>

          <a ref="scrollCue" href="#conversation" class="scroll-cue">
            <span>向下，重新接通</span><i><b></b></i>
          </a>
        </div>
      </section>

      <section id="conversation" class="conversation-screen screen-enter" aria-labelledby="conversation-title">
        <div class="page-shell conversation-layout">
          <div class="conversation-copy enter-left">
            <p class="story-overline"><span></span>RELATION CHANNEL / CONNECTED</p>
            <h2 id="conversation-title">熟悉的语气，<br />带着新的边界回来。</h2>
            <p>先在网页创建角色，愿意时再接入微信。</p>
            <dl class="conversation-functions">
              <div><dt>角色设定</dt><dd>性格、语气与相处边界</dd></div>
              <div><dt>关系记忆</dt><dd>称呼、经历与最近对话</dd></div>
              <div><dt>主动联系</dt><dd>接入微信后默认开启</dd></div>
            </dl>
            <a href="#features" class="conversation-link">查看完整功能 <span>↘</span></a>
          </div>

          <div class="conversation-reveal enter-right">
            <p class="phone-channel"><span></span>WECHAT CHANNEL / ONLINE</p>
            <div class="phone-device" role="img" aria-label="手机微信示例对话">
              <i class="phone-button phone-button-one"></i>
              <i class="phone-button phone-button-two"></i>
              <i class="phone-button phone-button-three"></i>
              <div class="phone-screen">
                <div class="phone-island" aria-hidden="true"><i></i></div>
                <div class="wechat-window">
                  <div class="wechat-statusbar">
                    <span>22:47</span>
                    <div><i></i><i></i><i></i><b></b></div>
                  </div>
                  <div class="wechat-header">
                    <button type="button" aria-hidden="true" tabindex="-1">‹</button>
                    <div><strong>{{ heroConversation.name }}</strong></div>
                    <button type="button" aria-hidden="true" tabindex="-1">•••</button>
                  </div>

                  <div class="wechat-body hero-wechat-body">
                    <p class="chat-time">{{ heroConversation.time }}</p>
                    <div
                      v-for="(message, index) in heroConversation.messages"
                      :key="index + '-' + message.text"
                      class="message-row"
                      :class="message.side"
                    >
                      <span
                        v-if="message.side === 'them'"
                        class="avatar"
                        :style="{ background: heroConversation.avatarColor }"
                      >{{ heroConversation.initial }}</span>
                      <div class="message-bubble">{{ message.text }}</div>
                      <span v-if="message.side === 'me'" class="avatar mine">我</span>
                    </div>

                    <div class="message-row them">
                      <span class="avatar" :style="{ background: heroConversation.avatarColor }">{{ heroConversation.initial }}</span>
                      <div class="message-bubble reply-message"><span class="reply-copy">{{ heroConversation.reply }}</span></div>
                    </div>
                  </div>

                  <div class="wechat-composer" aria-hidden="true">
                    <span class="voice-icon"></span>
                    <div>发消息…</div>
                    <span class="smile">⌣</span>
                    <span class="plus">+</span>
                  </div>
                </div>
              </div>
            </div>
            <p class="demo-caption"><span></span>示例对话来自角色设定，不代表任何真实人物。</p>
          </div>
        </div>
      </section>

      <section id="relationships" class="relationships-section screen-enter">
        <div class="page-shell">
          <div class="section-heading reveal">
            <p class="section-kicker">适合什么关系</p>
            <div>
              <h2>你想再聊一次的，<br />不一定只是“伴侣”。</h2>
              <p>它可以承接熟悉、遗憾、想象，也可以只是一段完全虚构的新关系。</p>
            </div>
          </div>

          <div class="relationship-list">
            <article
              v-for="(item, index) in relationshipCases"
              :key="item.title"
              class="relationship-item reveal"
              :style="{ '--delay': index * 70 + 'ms' }"
            >
              <span>0{{ index + 1 }}</span>
              <div>
                <h3>{{ item.title }}</h3>
                <p>{{ item.copy }}</p>
              </div>
              <small>{{ item.tag }}</small>
            </article>
          </div>

          <p class="relationship-disclaimer reveal">
            xyo.chat 创建的是基于你所提供设定的虚拟角色，不会读取、还原或代表任何现实中的人。
          </p>
        </div>
      </section>

      <section id="features" class="features-section screen-enter">
        <div class="page-shell">
          <div class="feature-heading reveal">
            <p class="section-kicker light">具体功能</p>
            <div>
              <h2>赛博前任，<br />不只会回消息。</h2>
              <p>从角色卡和分层记忆，到微信接入、主动联系、图片语音与持续变化的生活状态。</p>
            </div>
          </div>

          <div class="feature-grid">
            <article
              v-for="(feature, index) in features"
              :key="feature.title"
              class="feature-item reveal"
              :style="{ '--delay': index * 60 + 'ms' }"
            >
              <div class="feature-index">0{{ index + 1 }}</div>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.copy }}</p>
              <span>{{ feature.meta }}</span>
            </article>
          </div>
        </div>
      </section>

      <section id="how" class="how-section screen-enter">
        <div class="page-shell how-shell">
          <div class="how-copy reveal">
            <p class="section-kicker">如何开始</p>
            <h2>先写清楚这个人，<br />再决定去哪里聊。</h2>
            <p>
              不需要一次完成整套人设。名字、你们的关系、说话方式和几个不能碰的话题，已经足够开始。
            </p>
            <router-link :to="primaryRoute" class="inline-cta">
              {{ loggedIn ? "回到我的角色" : "从简易设定开始" }}
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M14 7l5 5-5 5" /></svg>
            </router-link>
          </div>

          <div class="steps-list">
            <article v-for="(step, index) in steps" :key="step.title" class="step reveal" :style="{ '--delay': index * 80 + 'ms' }">
              <span>{{ index + 1 }}</span>
              <div>
                <small>{{ step.overline }}</small>
                <h3>{{ step.title }}</h3>
                <p>{{ step.copy }}</p>
              </div>
            </article>
          </div>

          <aside class="character-sheet reveal" aria-label="角色设定示例">
            <div class="sheet-head">
              <span>角色设定片段</span><i>已保存</i>
            </div>
            <dl>
              <div><dt>名字</dt><dd>许青</dd></div>
              <div><dt>你们的关系</dt><dd>分开一年，偶尔还会想起彼此</dd></div>
              <div><dt>说话方式</dt><dd>短句，不说大道理，关心时会嘴硬</dd></div>
              <div><dt>相处边界</dt><dd>不冒充真人，不替我做决定</dd></div>
              <div><dt>生活痕迹</dt><dd>在杭州做设计，夜里常听旧歌</dd></div>
            </dl>
            <p><span></span>这些设定可以在以后继续修改。</p>
          </aside>
        </div>
      </section>

      <section id="boundaries" class="boundaries-section screen-enter">
        <div class="page-shell boundary-shell reveal">
          <div>
            <p class="section-kicker light">关系与边界</p>
            <h2>像一个熟悉的人，<br />不等于那个人真的回来。</h2>
          </div>
          <div class="boundary-copy">
            <p>
              角色可以拥有熟悉的语气、共同背景和持续记忆，但它仍然是由模型驱动的虚拟角色。
              我们更愿意把这一点说清楚，而不是制造“真人重现”的错觉。
            </p>
            <ul>
              <li><span>01</span>主动联系可以关闭</li>
              <li><span>02</span>当前会话记忆可以重置</li>
              <li><span>03</span>微信绑定可以解除</li>
              <li><span>04</span>角色资料和关系可以继续修改</li>
            </ul>
          </div>
        </div>
      </section>

      <section class="faq-section screen-enter">
        <div class="page-shell faq-shell">
          <div class="faq-heading reveal">
            <p class="section-kicker">常见问题</p>
            <h2>开始之前，<br />把重要的事说清楚。</h2>
          </div>

          <div class="faq-list reveal">
            <article
              v-for="(item, index) in faqItems"
              :key="item.question"
              class="faq-item"
              :class="{ active: activeFaqIndex === index }"
            >
              <button
                type="button"
                :aria-expanded="activeFaqIndex === index ? 'true' : 'false'"
                :aria-controls="'answer-' + index"
                @click="toggleFaq(index)"
              >
                <span>{{ item.question }}</span><i aria-hidden="true"></i>
              </button>
              <div
                :id="'answer-' + index"
                class="faq-answer"
                :aria-hidden="activeFaqIndex !== index ? 'true' : 'false'"
              >
                <p>{{ item.answer }}</p>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="final-section screen-enter">
        <div class="page-shell final-shell reveal">
          <div>
            <p>xyo.chat · 创建一个关系型虚拟角色</p>
            <h2>写下那个你还想再聊一次的人。</h2>
          </div>
          <div>
            <p>不是让过去重来，只是给未说完的话一个新的去处。</p>
            <router-link :to="primaryRoute" class="primary-button light-button">
              <span>{{ loggedIn ? "继续聊天" : "创建我的角色" }}</span>
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13M14 7l5 5-5 5" /></svg>
            </router-link>
          </div>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <div class="page-shell footer-inner">
        <router-link to="/home" class="footer-brand">xyo<span>.chat</span></router-link>
        <p>关系型虚拟角色 · 网页可聊 · 微信可选</p>
        <div><span>© 2026 xyo.chat</span><a href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">苏ICP备15020291号-2</a></div>
      </div>
    </footer>
  </div>
</template>

<script>
import { authApi, clearAuth, getToken, getUser, setAuth } from "@/services/api";

export default {
  name: "HomeView",
  data() {
    return {
      token: getToken(),
      currentUser: getUser(),
      pageReady: false,
      reduceMotion: false,
      revealObserver: null,
      activeFaqIndex: null,
      heroConversation: {
        name: "许青",
        relation: "赛博前任 · 基于你的设定",
        initial: "青",
        avatarColor: "#17b66c",
        time: "晚上 10:47",
        messages: [
          { side: "them", text: "今天是不是又路过那家店了？" },
          { side: "me", text: "嗯。还是没进去。" },
          { side: "them", text: "你以前总说，绕路就不算想起。" },
        ],
        reply: "不用急着证明你已经放下了。",
      },
      relationshipCases: [
        {
          title: "赛博前任",
          copy: "保留你写下的熟悉语气、关系张力和共同背景，带着明确的新边界重新开始。",
          tag: "旧事 · 记忆 · 新边界",
        },
        {
          title: "错过的旧友",
          copy: "把一段淡掉的友情、没来得及认识的人，写成可以重新开始对话的角色。",
          tag: "旧友 · 重逢 · 日常",
        },
        {
          title: "理想伴侣",
          copy: "由你决定亲密度、相处节奏和表达方式，不必套用现实中某一个人的样子。",
          tag: "恋爱 · 陪伴 · 分寸",
        },
        {
          title: "原创角色",
          copy: "把 OC、故事人物或一张角色卡变成能持续聊天、会形成共同记忆的对象。",
          tag: "设定 · 故事 · 共创",
        },
      ],
      features: [
        {
          title: "写成那个熟悉的 TA",
          copy: "名字、背景、脾气、口头禅、关系边界和雷点，都可以写进可复用的角色卡。",
          meta: "简易 / 专业两种模式",
        },
        {
          title: "记得，也会慢慢靠近",
          copy: "偏好、约定与共同经历会被选择性整理，长期和最近记忆都可以查看，当前会话可以重置。",
          meta: "长期 / 短期 / 可重置",
        },
        {
          title: "从网页开始，在微信继续",
          copy: "先在网页聊起来，再决定是否扫码接入微信；同一个角色可以从两个入口聊天，绑定也可以解除。",
          meta: "网页 / 微信两种入口",
        },
        {
          title: "不总是等你先开口",
          copy: "默认开启后，TA 可以追问、关心、提醒或开启新话题，也能安排图片与语音任务。",
          meta: "主动联系需绑定微信",
        },
        {
          title: "不只有文字",
          copy: "你可以发图片；TA 也能理解图片、生成或修改照片、发贴纸，或者用语音消息回复。",
          meta: "图片理解 / 生图 / 语音消息",
        },
        {
          title: "TA 也有自己的今天",
          copy: "所在地时间、天气、日程、着装和当前状态，会随着聊天形成连续的生活痕迹。",
          meta: "亲密度 / 状态 / 日程",
        },
      ],
      steps: [
        {
          overline: "角色卡",
          title: "写下 TA 是谁",
          copy: "一个名字、一段背景、一种说话方式，再补上你们的关系和不能越过的界限。",
        },
        {
          overline: "伴侣",
          title: "从网页开始相处",
          copy: "用角色卡创建可以聊天的对象。聊过的内容会逐渐形成这段关系自己的记忆。",
        },
        {
          overline: "微信 · 可选",
          title: "愿意时，再接入微信",
          copy: "绑定已有微信号、扫码新增，或暂时跳过；之后仍可以在详情页继续管理。",
        },
      ],
      faqItems: [
        {
          question: "可以创建虚拟前女友或前男友吗？",
          answer: "可以。你可以写下熟悉的关系、性格与说话方式，但生成的是基于这些设定的虚拟角色，并不是现实中那个人的意识、记忆或替身。",
        },
        {
          question: "一定要绑定微信吗？",
          answer: "不用。创建后可以直接在网页聊天，想接入时再绑定微信，之后也可以解除绑定。",
        },
        {
          question: "角色会一直主动给我发消息吗？",
          answer: "不会无限打扰。接入微信后主动联系默认开启，也可以随时关闭；问候、提醒、图片或语音任务同样可以取消。",
        },
        {
          question: "角色会记住什么？",
          answer: "系统会从对话中整理与关系有关的长期记忆，而不是机械背下每一句话。你可以查看详情，也能重置当前会话记忆。",
        },
      ],
    };
  },
  computed: {
    loggedIn() {
      return Boolean(this.token);
    },
    displayName() {
      return (this.currentUser && (this.currentUser.display_name || this.currentUser.email)) || "朋友";
    },
    primaryRoute() {
      return this.loggedIn ? "/chat" : "/login";
    },
  },
  created() {
    if (this.token) this.loadCurrentUser();
  },
  mounted() {
    this.reduceMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    window.requestAnimationFrame(() => {
      this._heroReadyTimer = window.setTimeout(() => {
        this.pageReady = true;
      }, this.reduceMotion ? 0 : 90);
    });
    this.setupHeroMotion();

    const elements = this.$el.querySelectorAll(".reveal");
    const screens = this.$el.querySelectorAll(".screen-enter");
    if (this.reduceMotion || !("IntersectionObserver" in window)) {
      elements.forEach((element) => element.classList.add("visible"));
      screens.forEach((screen) => screen.classList.add("is-inview"));
      return;
    }

    this.revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.1) {
            entry.target.classList.add("visible");
          } else if (!entry.isIntersecting) {
            entry.target.classList.remove("visible");
          }
        });
      },
      { threshold: [0, 0.1], rootMargin: "0px 0px -6%" }
    );
    elements.forEach((element) => this.revealObserver.observe(element));

    this.screenObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio >= 0.18) {
            entry.target.classList.add("is-inview");
          } else if (!entry.isIntersecting) {
            entry.target.classList.remove("is-inview");
          }
        });
      },
      { threshold: [0, 0.18], rootMargin: "0px 0px -5%" }
    );
    screens.forEach((screen) => this.screenObserver.observe(screen));
  },
  beforeDestroy() {
    if (this._heroReadyTimer) window.clearTimeout(this._heroReadyTimer);
    this.teardownHeroMotion();
    if (this.revealObserver) this.revealObserver.disconnect();
    if (this.screenObserver) this.screenObserver.disconnect();
  },
  methods: {
    async loadCurrentUser() {
      try {
        const user = await authApi.me();
        this.token = getToken();
        this.currentUser = user;
        setAuth(null, user);
      } catch (error) {
        clearAuth();
        this.token = "";
        this.currentUser = null;
      }
    },
    logout() {
      clearAuth();
      this.token = "";
      this.currentUser = null;
    },
    setupHeroMotion() {
      const section = this.$refs.heroSection;
      const pin = this.$refs.heroPin;
      if (!section || !pin || typeof window === "undefined") return;

      this._heroSection = section;
      this._heroPin = pin;
      this._heroProgress = 0;
      this._heroCanvasVisible = true;
      this._heroMedia = window.matchMedia("(max-width: 840px)");
      this._heroMotionDisabled = this.reduceMotion || this._heroMedia.matches;

      this._heroResizeHandler = () => {
        if (this._heroResizeFrame) return;
        this._heroResizeFrame = window.requestAnimationFrame(() => {
          this._heroResizeFrame = null;
          if (!this._heroMotionDisabled) this.resizeHeroCanvas();
        });
      };

      this._heroMediaHandler = () => {
        const disabled = this.reduceMotion || this._heroMedia.matches;
        if (disabled === this._heroMotionDisabled) return;
        this._heroMotionDisabled = disabled;
        if (disabled) {
          this.stopHeroCanvas();
        } else {
          this.initHeroCanvas();
          this.resizeHeroCanvas();
          this.startHeroCanvas();
        }
      };

      this._heroVisibilityHandler = () => {
        if (document.hidden) this.stopHeroCanvas();
        else this.startHeroCanvas();
      };

      window.addEventListener("resize", this._heroResizeHandler, { passive: true });
      document.addEventListener("visibilitychange", this._heroVisibilityHandler);
      if (this._heroMedia.addEventListener) this._heroMedia.addEventListener("change", this._heroMediaHandler);
      else if (this._heroMedia.addListener) this._heroMedia.addListener(this._heroMediaHandler);

      if (!this._heroMotionDisabled) this.initHeroCanvas();
    },
    updateHeroProgress() {
      if (!this._heroSection || !this._heroPin) return;

      if (this._heroMotionDisabled) {
        this.applyHeroProgress(1);
        return;
      }

      const rect = this._heroSection.getBoundingClientRect();
      const headerHeight = window.innerWidth <= 560 ? 64 : 72;
      const sectionTop = window.scrollY + rect.top;
      const visibleHeight = Math.max(420, window.innerHeight - headerHeight);
      const travel = Math.max(1, this._heroSection.offsetHeight - visibleHeight);
      const progress = Math.max(0, Math.min(1, (window.scrollY - (sectionTop - headerHeight)) / travel));
      this.applyHeroProgress(progress);
    },
    applyHeroProgress(progress) {
      if (!this._heroPin) return;
      const clamp = (value) => Math.max(0, Math.min(1, value));
      const smooth = (value) => {
        const point = clamp(value);
        return point * point * (3 - 2 * point);
      };
      const introOut = smooth((progress - 0.1) / 0.22);
      const whisper = smooth((progress - 0.16) / 0.2);
      const chat = smooth((progress - 0.04) / 0.24);
      const messageOne = 1;
      const messageTwo = 1;
      const messageThree = smooth((progress - 0.4) / 0.12);
      const typingIn = smooth((progress - 0.58) / 0.07);
      const typingOut = smooth((progress - 0.75) / 0.05);
      const typing = typingIn * (1 - typingOut);
      const reply = smooth((progress - 0.75) / 0.1);
      const replyRow = Math.max(typing, reply);
      const scrollCueOut = smooth((progress - 0.05) / 0.14);
      const style = this._heroPin.style;
      const introHidden = !this._heroMotionDisabled && introOut > 0.98;
      const cueHidden = this._heroMotionDisabled || scrollCueOut > 0.98;

      if (this.$refs.heroIntro) {
        this.$refs.heroIntro.toggleAttribute("inert", introHidden);
        this.$refs.heroIntro.setAttribute("aria-hidden", introHidden ? "true" : "false");
        this.$refs.heroIntro.style.pointerEvents = introHidden ? "none" : "";
      }
      if (this.$refs.scrollCue) {
        this.$refs.scrollCue.toggleAttribute("inert", cueHidden);
        this.$refs.scrollCue.setAttribute("aria-hidden", cueHidden ? "true" : "false");
        this.$refs.scrollCue.style.pointerEvents = cueHidden ? "none" : "";
      }

      this._heroProgress = progress;
      style.setProperty("--hero-progress", progress.toFixed(4));
      style.setProperty("--hero-pin-opacity", "1");
      style.setProperty("--hero-copy-opacity", (1 - introOut).toFixed(3));
      style.setProperty("--hero-copy-y", `${(-18 * introOut).toFixed(2)}px`);
      style.setProperty("--hero-copy-blur", `${(5 * introOut).toFixed(2)}px`);
      style.setProperty("--story-opacity", whisper.toFixed(3));
      style.setProperty("--story-y", `${(14 * (1 - whisper)).toFixed(2)}px`);
      style.setProperty("--story-blur", `${(3 * (1 - whisper)).toFixed(2)}px`);
      style.setProperty("--chat-opacity", "1");
      style.setProperty("--chat-y", `${(7 * (1 - chat)).toFixed(2)}px`);
      style.setProperty("--chat-scale", (0.99 + chat * 0.01).toFixed(4));
      style.setProperty("--message-one", messageOne.toFixed(3));
      style.setProperty("--message-two", messageTwo.toFixed(3));
      style.setProperty("--message-three", messageThree.toFixed(3));
      style.setProperty("--message-one-y", `${(10 * (1 - messageOne)).toFixed(2)}px`);
      style.setProperty("--message-two-y", `${(10 * (1 - messageTwo)).toFixed(2)}px`);
      style.setProperty("--message-three-y", `${(10 * (1 - messageThree)).toFixed(2)}px`);
      style.setProperty("--typing-opacity", typing.toFixed(3));
      style.setProperty("--reply-opacity", reply.toFixed(3));
      style.setProperty("--reply-row-opacity", replyRow.toFixed(3));
      style.setProperty("--reply-width", `${(46 + reply * 150).toFixed(2)}px`);
      style.setProperty("--reply-y", `${(10 * (1 - reply)).toFixed(2)}px`);
      style.setProperty("--scroll-cue-opacity", (1 - scrollCueOut).toFixed(3));
      style.setProperty("--ribbon-shift", `${(-22 * progress).toFixed(2)}px`);
      style.setProperty("--ribbon-warmth", (0.22 + progress * 0.48).toFixed(3));
    },
    initHeroCanvas() {
      if (this._heroMotionDisabled || this._heroCanvasContext || !this.$refs.heroCanvas) return;
      const canvas = this.$refs.heroCanvas;
      const context = canvas.getContext("2d", { alpha: true });
      if (!context) return;

      this._heroCanvasContext = context;
      this._heroCanvasMotes = Array.from({ length: 32 }, (_, index) => ({
        x: ((index * 37) % 101) / 100,
        y: 0.28 + (((index * 19) % 47) / 100),
        radius: 0.7 + (index % 4) * 0.42,
        drift: 7 + (index % 6) * 2.4,
        speed: 0.00009 + (index % 5) * 0.000018,
        phase: index * 0.83,
        alpha: 0.035 + (index % 5) * 0.012,
        warm: index % 3 === 0,
      }));
      this._heroCanvasStreaks = Array.from({ length: 10 }, (_, index) => ({
        x: 0.04 + ((index * 29) % 42) / 100,
        y: ((index * 17) % 86) / 100,
        length: 42 + (index % 5) * 12,
        speed: 0.000035 + (index % 4) * 0.000012,
        alpha: 0.025 + (index % 4) * 0.012,
      }));
      this._drawHeroFrame = (time) => this.drawHeroCanvas(time);
      this.resizeHeroCanvas();

      if ("IntersectionObserver" in window) {
        this._heroCanvasObserver = new IntersectionObserver(
          (entries) => {
            this._heroCanvasVisible = entries.some((entry) => entry.isIntersecting);
            if (this._heroCanvasVisible) this.startHeroCanvas();
            else this.stopHeroCanvas();
          },
          { rootMargin: "120px 0px" }
        );
        this._heroCanvasObserver.observe(this._heroSection);
      }
      this.startHeroCanvas();
    },
    resizeHeroCanvas() {
      const canvas = this.$refs.heroCanvas;
      const context = this._heroCanvasContext;
      if (!canvas || !context || this._heroMotionDisabled) return;
      const rect = this._heroPin.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const ratio = Math.min(window.devicePixelRatio || 1, 1.25);
      if (canvas.width === Math.round(width * ratio) && canvas.height === Math.round(height * ratio)) return;

      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      this._heroCanvasSize = { width, height, ratio };

      const leftGlow = context.createRadialGradient(width * 0.2, height * 0.5, 0, width * 0.2, height * 0.5, width * 0.52);
      leftGlow.addColorStop(0, "rgba(215, 255, 99, 0.2)");
      leftGlow.addColorStop(0.46, "rgba(23, 182, 108, 0.07)");
      leftGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
      const rightGlow = context.createRadialGradient(width * 0.79, height * 0.5, 0, width * 0.79, height * 0.5, width * 0.45);
      rightGlow.addColorStop(0, "rgba(143, 103, 72, 0.17)");
      rightGlow.addColorStop(0.5, "rgba(215, 255, 99, 0.07)");
      rightGlow.addColorStop(1, "rgba(0, 0, 0, 0)");
      this._heroCanvasLeftGlow = leftGlow;
      this._heroCanvasRightGlow = rightGlow;
    },
    drawHeroCanvas(time = 0) {
      this._heroCanvasFrame = null;
      if (
        this._heroMotionDisabled ||
        !this._heroCanvasVisible ||
        document.hidden ||
        !this._heroCanvasContext ||
        !this._heroCanvasSize
      ) return;

      if (time - (this._heroCanvasLastFrame || 0) < 44) {
        this._heroCanvasFrame = window.requestAnimationFrame(this._drawHeroFrame);
        return;
      }
      this._heroCanvasLastFrame = time;

      const context = this._heroCanvasContext;
      const { width, height, ratio } = this._heroCanvasSize;
      const progress = this._heroProgress || 0;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, width, height);
      context.globalCompositeOperation = "screen";
      context.fillStyle = this._heroCanvasLeftGlow;
      context.fillRect(0, 0, width, height);
      context.globalAlpha = 0.72 + progress * 0.18;
      context.fillStyle = this._heroCanvasRightGlow;
      context.fillRect(0, 0, width, height);

      this._heroCanvasStreaks.forEach((streak, index) => {
        const y = ((streak.y * height + time * streak.speed * height + index * 13) % (height + streak.length)) - streak.length;
        context.beginPath();
        context.moveTo(streak.x * width, y);
        context.lineTo(streak.x * width - 12, y + streak.length);
        context.strokeStyle = `rgba(23, 182, 108, ${streak.alpha * 1.35})`;
        context.lineWidth = 0.7;
        context.stroke();
      });

      this._heroCanvasMotes.forEach((mote) => {
        const x = mote.x * width + Math.sin(time * mote.speed + mote.phase) * mote.drift;
        const y = mote.y * height + Math.cos(time * mote.speed * 0.72 + mote.phase) * mote.drift * 0.55;
        context.beginPath();
        context.arc(x, y, mote.radius, 0, Math.PI * 2);
        context.fillStyle = mote.warm
          ? `rgba(143, 103, 72, ${mote.alpha * 1.15 + progress * 0.03})`
          : `rgba(23, 182, 108, ${mote.alpha})`;
        context.fill();
      });

      context.globalAlpha = 0.18 + progress * 0.12;
      for (let index = 0; index < 3; index += 1) {
        const y = height * (0.44 + index * 0.055) + Math.sin(time * 0.0003 + index) * 5;
        context.beginPath();
        context.moveTo(-20, y);
        context.bezierCurveTo(width * 0.28, y - 12, width * 0.7, y + 10, width + 20, y - 3);
        context.strokeStyle = index === 1 ? "rgba(215, 255, 99, 0.38)" : "rgba(23, 182, 108, 0.18)";
        context.lineWidth = index === 1 ? 1.1 : 0.65;
        context.stroke();
      }
      context.globalAlpha = 1;
      context.globalCompositeOperation = "source-over";
      this._heroCanvasFrame = window.requestAnimationFrame(this._drawHeroFrame);
    },
    startHeroCanvas() {
      if (
        this._heroMotionDisabled ||
        !this._drawHeroFrame ||
        this._heroCanvasFrame ||
        !this._heroCanvasVisible ||
        document.hidden
      ) return;
      this._heroCanvasFrame = window.requestAnimationFrame(this._drawHeroFrame);
    },
    stopHeroCanvas() {
      if (this._heroCanvasFrame) window.cancelAnimationFrame(this._heroCanvasFrame);
      this._heroCanvasFrame = null;
    },
    teardownHeroMotion() {
      if (typeof window === "undefined") return;
      this.stopHeroCanvas();
      if (this._heroScrollFrame) window.cancelAnimationFrame(this._heroScrollFrame);
      if (this._heroResizeFrame) window.cancelAnimationFrame(this._heroResizeFrame);
      if (this._heroScrollHandler) window.removeEventListener("scroll", this._heroScrollHandler);
      if (this._heroResizeHandler) window.removeEventListener("resize", this._heroResizeHandler);
      if (this._heroVisibilityHandler) document.removeEventListener("visibilitychange", this._heroVisibilityHandler);
      if (this._heroMedia && this._heroMediaHandler) {
        if (this._heroMedia.removeEventListener) this._heroMedia.removeEventListener("change", this._heroMediaHandler);
        else if (this._heroMedia.removeListener) this._heroMedia.removeListener(this._heroMediaHandler);
      }
      if (this._heroCanvasObserver) this._heroCanvasObserver.disconnect();
      this._heroCanvasObserver = null;
      this._heroCanvasContext = null;
    },
    toggleFaq(index) {
      this.activeFaqIndex = this.activeFaqIndex === index ? null : index;
    },
  },
};
</script>

<style scoped lang="scss">
.home {
  --paper: #f5f1e8;
  --paper-soft: #e5f0e6;
  --ink: #17281f;
  --muted: #526257;
  --wine: #0e8f55;
  --wine-dark: #0a6c41;
  --wine-light: #d7ff63;
  --coral: #17b66c;
  --coral-bright: #d7ff63;
  --heat: #d7ff63;
  --apricot: #8f6748;
  --afterimage: #17b66c;
  --afterimage-deep: #0f7f4d;
  --night: #17281f;
  --night-soft: #20372a;
  --raised: #294735;
  --line: rgba(23, 40, 31, 0.16);
  --dark-line: rgba(248, 245, 235, 0.15);
  --on-dark: #f8f5eb;
  --on-dark-muted: #bdcabf;
  --wechat: #ededed;
  --wechat-green: #95ec69;
  --font-serif: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-sans: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", -apple-system, BlinkMacSystemFont, sans-serif;

  min-height: 100vh;
  overflow-x: clip;
  overflow-y: visible;
  background: var(--paper);
  color: var(--ink);
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;

  * { box-sizing: border-box; }
  a { color: inherit; text-decoration: none; }
  button { font: inherit; }
  ::selection { background: var(--coral-bright); color: var(--ink); }
}

.page-shell,
.header-inner {
  width: min(100% - 48px, 1240px);
  margin: 0 auto;
}

.site-header {
  position: sticky;
  top: 0;
  z-index: 50;
  border-bottom: 1px solid var(--dark-line);
  background: rgba(14, 13, 24, 0.94);
  color: var(--on-dark);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);

  &::after {
    position: absolute;
    right: 0;
    bottom: -1px;
    left: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent 8%, rgba(101,116,255,0.65) 34%, rgba(255,93,95,0.82) 68%, rgba(255,179,106,0.58) 88%, transparent);
    content: "";
    pointer-events: none;
  }
}

.header-inner {
  display: flex;
  align-items: center;
  height: 72px;
  gap: 48px;
}

.brand {
  display: inline-flex;
  align-items: baseline;
  flex: 0 0 auto;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.07em;
  text-shadow: 0 0 24px rgba(255, 93, 95, 0.2);

  small {
    margin-left: 3px;
    font-family: var(--font-sans);
    font-size: 9px;
    font-weight: 500;
    letter-spacing: 0.08em;
  }
}

.header-nav {
  display: flex;
  align-items: center;
  margin-right: auto;
  gap: 34px;

  a {
    position: relative;
    padding: 8px 0;
    color: var(--on-dark-muted);
    font-size: 13px;
    transition: color 0.25s ease;

    &::after {
      position: absolute;
      right: 0;
      bottom: 2px;
      left: 0;
      height: 1px;
      background: var(--coral-bright);
      content: "";
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.25s ease;
    }

    &:hover,
    &:focus-visible { color: #fff; }
    &:hover::after,
    &:focus-visible::after { transform: scaleX(1); transform-origin: left; }
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 13px;
}

.user-name {
  max-width: 130px;
  overflow: hidden;
  color: rgba(248, 244, 239, 0.58);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.login,
.logout {
  padding: 10px 0;
  border: 0;
  background: transparent;
  color: rgba(248, 244, 239, 0.78);
  cursor: pointer;
}

.header-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 20px;
  border: 1px solid rgba(255, 93, 95, 0.72);
  border-radius: 999px;
  box-shadow: inset 0 0 0 1px rgba(101, 116, 255, 0.08);
  transition: background 0.25s ease, border-color 0.25s ease, color 0.25s ease, transform 0.25s ease;

  &:hover,
  &:focus-visible {
    border-color: transparent;
    background: var(--coral-bright);
    color: var(--night);
    transform: translateY(-1px);
  }
}

.eyebrow {
  display: flex;
  align-items: center;
  margin: 0 0 24px;
  color: var(--on-dark-muted);
  font-size: 11px;
  letter-spacing: 0.13em;

  span {
    width: 28px;
    height: 2px;
    margin-right: 10px;
    background: linear-gradient(90deg, var(--afterimage), var(--coral-bright));
    box-shadow: 0 0 12px rgba(101, 116, 255, 0.38);
  }
}

.hero-lead {
  max-width: 560px;
  margin: 24px 0 0;
  color: rgba(248, 244, 239, 0.68);
  font-size: 16px;
  line-height: 1.9;
}

.hero-actions {
  display: flex;
  align-items: center;
  margin-top: 32px;
  gap: 24px;
}

.primary-button {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  min-width: 210px;
  min-height: 52px;
  padding: 0 18px 0 22px;
  border-radius: 999px;
  background: var(--coral-bright);
  box-shadow: 0 12px 30px rgba(255, 93, 95, 0.24), 0 0 0 1px rgba(255, 248, 243, 0.18) inset;
  color: #170c12 !important;
  font-size: 14px;
  transition: transform 0.25s ease, filter 0.25s ease, box-shadow 0.25s ease;

  svg {
    width: 19px;
    margin-left: 18px;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.6;
    transition: transform 0.25s ease;
  }

  &:hover,
  &:focus-visible {
    filter: brightness(1.08);
    box-shadow: 0 16px 38px rgba(255, 93, 95, 0.32), 0 0 0 1px rgba(255, 248, 243, 0.24) inset;
    transform: translateY(-2px);
  }
  &:hover svg,
  &:focus-visible svg { transform: translateX(4px); }
}

.quiet-link {
  position: relative;
  padding: 10px 0;
  color: var(--on-dark);
  font-size: 13px;

  &::after {
    position: absolute;
    right: 0;
    bottom: 5px;
    left: 0;
    height: 1px;
    background: linear-gradient(90deg, var(--afterimage), var(--coral-bright));
    content: "";
    transform: scaleX(0.35);
    transform-origin: left;
    transition: transform 0.25s ease;
  }

  &:hover::after,
  &:focus-visible::after { transform: scaleX(1); }
}

.honest-note {
  display: flex;
  align-items: center;
  margin: 20px 0 0;
  color: var(--on-dark-muted);
  font-size: 11px;
  line-height: 1.6;

  i {
    flex: 0 0 auto;
    width: 6px;
    height: 6px;
    margin-right: 9px;
    border: 1px solid var(--afterimage);
    border-radius: 50%;
    box-shadow: 0 0 9px rgba(101, 116, 255, 0.42);
  }
}

.stage-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  color: rgba(248, 244, 239, 0.38);
  font-size: 9px;
  letter-spacing: 0.14em;

  em { font-style: normal; letter-spacing: 0.06em; }
}

.wechat-window {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 20px;
  background: var(--wechat);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.28);
  color: #1d1d1d;
}

.wechat-statusbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 26px;
  padding: 0 17px;
  background: #f7f7f7;
  font-size: 9px;

  > div { display: flex; align-items: flex-end; gap: 2px; }
  i { display: block; width: 2px; background: #222; }
  i:nth-child(1) { height: 4px; }
  i:nth-child(2) { height: 6px; }
  i:nth-child(3) { height: 8px; }
  b { display: block; width: 13px; height: 7px; margin-left: 3px; border: 1px solid #222; border-radius: 2px; }
}

.wechat-header {
  display: grid;
  grid-template-columns: 36px 1fr 36px;
  align-items: center;
  min-height: 60px;
  padding: 0 12px;
  border-bottom: 1px solid #dfdfdf;
  background: #f7f7f7;

  button {
    border: 0;
    background: transparent;
    color: #222;
    font-size: 28px;
    pointer-events: none;
  }

  button:last-child { font-size: 15px; letter-spacing: 2px; }

  > div { text-align: center; }
  strong { display: block; font-size: 15px; font-weight: 500; }
  span { display: block; margin-top: 2px; color: #666; font-size: 9px; }
}

.wechat-body {
  min-height: 455px;
  padding: 20px 16px 24px;
  background-color: var(--wechat);
  background-image: linear-gradient(rgba(255,255,255,0.2) 1px, transparent 1px);
  background-size: 100% 40px;
}

.chat-time {
  margin: 0 0 18px;
  color: #666;
  font-size: 10px;
  text-align: center;
}

.message-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 9px;
  opacity: 0;
  transform: translateY(9px);
  animation: message-in 0.42s ease forwards;
  animation-delay: var(--message-delay, 0ms);

  &.me { justify-content: flex-end; }
}

@keyframes message-in { to { opacity: 1; transform: translateY(0); } }

.avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  border-radius: 4px;
  color: #fff;
  font-family: var(--font-serif);
  font-size: 14px;

  &.mine { background: #6574ff; font-family: var(--font-sans); font-size: 11px; }
}

.message-bubble {
  position: relative;
  max-width: 74%;
  min-height: 34px;
  padding: 9px 12px;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
  font-size: 13px;
  line-height: 1.58;

  &::before {
    position: absolute;
    top: 11px;
    left: -5px;
    width: 10px;
    height: 10px;
    background: inherit;
    content: "";
    transform: rotate(45deg);
  }
}

.me .message-bubble {
  background: var(--wechat-green);

  &::before { right: -5px; left: auto; }
}

@keyframes typing-dot { 0%, 60%, 100% { transform: translateY(0); opacity: 0.45; } 30% { transform: translateY(-3px); opacity: 1; } }

.wechat-composer {
  display: grid;
  grid-template-columns: 28px 1fr 26px 28px;
  align-items: center;
  min-height: 58px;
  padding: 8px 12px;
  border-top: 1px solid #dadada;
  background: #f7f7f7;
  gap: 8px;

  > div {
    display: flex;
    align-items: center;
    min-height: 38px;
    padding: 0 12px;
    border-radius: 4px;
    background: #fff;
    color: #737373;
    font-size: 12px;
  }

  .voice-icon {
    width: 23px;
    height: 23px;
    border: 1.5px solid #444;
    border-radius: 50%;
    position: relative;
  }
  .voice-icon::after { position: absolute; top: 6px; left: 7px; width: 7px; height: 8px; border-right: 1.5px solid #444; border-left: 1.5px solid #444; content: ""; }
  .smile { font-size: 25px; line-height: 1; transform: rotate(180deg); }
  .plus { display: inline-flex; align-items: center; justify-content: center; width: 23px; height: 23px; border: 1.5px solid #444; border-radius: 50%; font-size: 18px; line-height: 1; }
}

.demo-caption {
  display: flex;
  align-items: center;
  margin: 12px 0 0;
  color: rgba(255, 228, 233, 0.66);
  font-size: 9px;
  line-height: 1.5;

  span { flex: 0 0 auto; width: 18px; height: 1px; margin-right: 8px; background: var(--apricot); }
}

/* 首屏是一份重新接通的关系档案：左侧身份与控制权，右侧真实比例的手机微信。 */
.hero {
  position: relative;
  height: 160svh;
  min-height: 1080px;
  overflow: visible;
  background: var(--night);
  color: var(--on-dark);
}

.hero-pin {
  --hero-pin-opacity: 1;
  --hero-copy-opacity: 1;
  --hero-copy-y: 0px;
  --hero-copy-blur: 0px;
  --story-opacity: 0;
  --story-y: 14px;
  --story-blur: 3px;
  --chat-opacity: 0.88;
  --chat-y: 18px;
  --chat-scale: 0.975;
  --chat-blur: 2px;
  --message-one: 1;
  --message-two: 0;
  --message-three: 0;
  --message-one-y: 0px;
  --message-two-y: 10px;
  --message-three-y: 10px;
  --typing-opacity: 0;
  --reply-opacity: 0;
  --reply-row-opacity: 0;
  --reply-width: 46px;
  --reply-y: 10px;
  --scroll-cue-opacity: 1;
  --ribbon-shift: 0px;
  --ribbon-warmth: 0.2;

  position: sticky;
  top: 72px;
  z-index: 1;
  height: calc(100svh - 72px);
  min-height: 620px;
  overflow: hidden;
  background:
    radial-gradient(700px 470px at 82% 52%, rgba(255, 93, 95, 0.18), transparent 72%),
    radial-gradient(640px 420px at 15% 48%, rgba(101, 116, 255, 0.1), transparent 74%),
    var(--night);
  opacity: var(--hero-pin-opacity);
  isolation: isolate;
}

.hero-atmosphere,
.hero-night {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.hero-atmosphere { z-index: 2; }
.hero-night { z-index: 1; overflow: hidden; }

.ambient-vignette {
  position: absolute;
  inset: -12%;
  background:
    radial-gradient(ellipse at 50% 48%, transparent 0 38%, rgba(14,13,24,0.12) 67%, rgba(5,5,10,0.76) 100%),
    linear-gradient(180deg, rgba(7,7,12,0.42), transparent 30%, transparent 72%, rgba(7,7,12,0.66));
}

.night-grain {
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient(0deg, rgba(255,255,255,0.018) 0, rgba(255,255,255,0.018) 1px, transparent 1px, transparent 3px),
    repeating-linear-gradient(91deg, transparent 0, transparent 7px, rgba(255,255,255,0.012) 8px);
  opacity: 0.2;
  mix-blend-mode: soft-light;
}

.memory-ribbon {
  position: absolute;
  top: 50%;
  right: -8%;
  left: -8%;
  height: clamp(140px, 23vh, 210px);
  overflow: hidden;
  background:
    radial-gradient(ellipse at 76% 50%, rgba(255,93,95,var(--ribbon-warmth)), transparent 44%),
    radial-gradient(ellipse at 20% 52%, rgba(101,116,255,0.22), transparent 48%),
    linear-gradient(90deg, transparent 0%, rgba(101,116,255,0.1) 18%, rgba(255,93,95,0.24) 62%, rgba(255,179,106,0.16) 82%, transparent 100%);
  clip-path: inset(0 50% round 50%);
  filter: blur(8px) saturate(1.08);
  opacity: 0;
  transform: translate3d(0, calc(-50% + var(--ribbon-shift)), 0);
  transition: clip-path 1.08s cubic-bezier(0.16, 1, 0.3, 1) 0.26s, opacity 0.5s ease 0.2s;

  i { position: absolute; display: block; pointer-events: none; }
  i:nth-child(1) {
    top: 35%;
    right: 8%;
    left: 8%;
    height: 31%;
    border-radius: 50%;
    background: linear-gradient(90deg, transparent, rgba(101,116,255,0.2) 24%, rgba(255,93,95,0.34) 70%, transparent);
    filter: blur(18px);
    animation: ribbon-breathe 7s ease-in-out infinite alternate;
  }
  i:nth-child(2) {
    top: 50%;
    right: 5%;
    left: 5%;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(101,116,255,0.46), rgba(255,93,95,0.78), rgba(255,179,106,0.6), transparent);
    box-shadow: 0 0 24px rgba(255,93,95,0.34);
    opacity: 0.82;
  }
  i:nth-child(3) {
    top: 27%;
    right: 22%;
    width: 210px;
    height: 120px;
    border-radius: 50%;
    background: rgba(255,93,95,0.24);
    filter: blur(44px);
    animation: warm-drift 9s ease-in-out infinite alternate;
  }
}

.ready .memory-ribbon { clip-path: inset(0 0 round 0%); opacity: 1; }

@keyframes ribbon-breathe {
  from { opacity: 0.44; transform: scaleX(0.97) translateY(3px); }
  to { opacity: 0.86; transform: scaleX(1.03) translateY(-3px); }
}

@keyframes warm-drift {
  from { transform: translate3d(-18px, 5px, 0) scale(0.92); opacity: 0.5; }
  to { transform: translate3d(22px, -4px, 0) scale(1.08); opacity: 0.9; }
}

.hero-intro {
  position: absolute;
  top: 50%;
  left: max(24px, calc((100vw - 1160px) / 2));
  z-index: 5;
  width: min(560px, 47vw);
  opacity: var(--hero-copy-opacity);
  filter: blur(var(--hero-copy-blur));
  transform: translate3d(0, calc(-50% + var(--hero-copy-y)), 0);

  > * {
    opacity: 0;
    transform: translateY(13px);
    transition: opacity 0.68s cubic-bezier(0.22,1,0.36,1), transform 0.68s cubic-bezier(0.22,1,0.36,1);
  }
}

.ready .hero-intro {
  > * { opacity: 1; transform: translateY(0); }
  > :nth-child(1) { transition-delay: 0.36s; }
  > :nth-child(2) { transition-delay: 0.46s; }
  > :nth-child(3) { transition-delay: 0.56s; }
  > :nth-child(4) { transition-delay: 0.66s; }
  > :nth-child(5) { transition-delay: 0.74s; }
}

.hero-intro .eyebrow { margin-bottom: 20px; }

.hero-intro h1 {
  max-width: 560px;
  margin: 0;
  font-family: var(--font-sans);
  font-size: clamp(38px, 3.5vw, 50px);
  font-weight: 600;
  letter-spacing: -0.045em;
  line-height: 1.2;
  text-shadow: 0 12px 40px rgba(5,5,10,0.52);
}

.hero-intro .hero-lead {
  max-width: 510px;
  margin-top: 23px;
  color: var(--on-dark-muted);
  font-size: 14px;
  line-height: 1.8;
}

.hero-signal-list {
  position: relative;
  max-width: 510px;
  margin-top: 22px;
  overflow: hidden;
  border-top: 1px solid var(--dark-line);

  &::after {
    position: absolute;
    top: 0;
    left: -220px;
    width: 220px;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--afterimage), var(--heat), transparent);
    content: "";
    opacity: 0.48;
    animation: signal-scan 8s ease-in-out infinite;
  }

  > div {
    display: grid;
    grid-template-columns: 34px 1fr;
    align-items: center;
    min-height: 39px;
    border-bottom: 1px solid var(--dark-line);
  }

  > div > span { color: var(--afterimage); font-family: Georgia, serif; font-size: 9px; }
  p { display: grid; grid-template-columns: 96px 1fr; align-items: center; margin: 0; }
  b { color: var(--on-dark); font-size: 11px; font-weight: 500; letter-spacing: 0.06em; }
  small { color: var(--on-dark-muted); font-size: 11px; }
}

@keyframes signal-scan {
  0%, 72% { transform: translateX(0); opacity: 0; }
  78% { opacity: 0.48; }
  94%, 100% { transform: translateX(730px); opacity: 0; }
}

.hero-story {
  position: absolute;
  inset: 0;
  z-index: 6;
  pointer-events: none;
}

.story-whisper {
  position: absolute;
  top: 50%;
  left: max(24px, calc((100vw - 1160px) / 2));
  width: min(520px, 46vw);
  margin: 0;
  color: var(--on-dark);
  opacity: var(--story-opacity);
  filter: blur(var(--story-blur));
  transform: translate3d(0, calc(-50% + var(--story-y)), 0);

  h2 { margin: 18px 0 0; font-size: clamp(27px, 2.5vw, 35px); font-weight: 600; letter-spacing: -0.035em; line-height: 1.32; }
}

.story-overline {
  display: flex;
  align-items: center;
  margin: 0;
  color: var(--afterimage);
  font-size: 10px;
  letter-spacing: 0.14em;

  span { width: 28px; height: 1px; margin-right: 10px; background: var(--heat); }
}

.story-copy { max-width: 490px; margin: 20px 0 0; color: var(--on-dark-muted); font-size: 14px; line-height: 1.85; }

.story-status {
  max-width: 500px;
  margin: 28px 0 0;
  border-top: 1px solid var(--dark-line);

  > div { display: grid; grid-template-columns: 112px 1fr; align-items: center; min-height: 48px; border-bottom: 1px solid var(--dark-line); }
  dt { color: var(--afterimage); font-size: 9px; letter-spacing: 0.13em; }
  dd { margin: 0; color: var(--on-dark); font-size: 12px; }
}

.conversation-reveal {
  position: absolute;
  top: 50%;
  right: max(40px, calc((100vw - 1160px) / 2 + 16px));
  width: 330px;
  opacity: var(--chat-opacity);
  transform: translate3d(0, calc(-50% + var(--chat-y)), 0) scale(var(--chat-scale));
  transform-origin: center;
  will-change: opacity, transform;
}

.phone-channel {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: 0 4px 8px 0;
  color: var(--on-dark-muted);
  font-size: 9px;
  letter-spacing: 0.13em;

  span { width: 26px; height: 1px; margin-right: 8px; background: var(--heat); }
}

.phone-device {
  position: relative;
  height: min(620px, calc(100svh - 145px));
  margin-left: auto;
  aspect-ratio: 390 / 844;
  padding: 7px;
  border: 1px solid #3a3550;
  border-radius: 44px;
  background: #15131f;
  box-shadow: 0 36px 84px rgba(0,0,0,0.52), 0 0 0 2px rgba(255,255,255,0.025) inset, 18px 0 70px rgba(255,93,95,0.13);
  opacity: 0;
  transform: translateY(28px) rotate(1.4deg) scale(0.965);
  transition: opacity 0.72s ease 0.14s, transform 0.72s cubic-bezier(0.2,0.8,0.2,1) 0.14s;
}

.ready .phone-device { opacity: 1; transform: translateY(0) rotate(0) scale(1); }

.phone-button { position: absolute; left: -4px; width: 3px; border-radius: 3px 0 0 3px; background: #5e5773; }
.phone-button-one { top: 92px; height: 28px; }
.phone-button-two { top: 138px; height: 48px; }
.phone-button-three { top: 198px; height: 48px; }

.phone-screen {
  position: relative;
  height: 100%;
  overflow: hidden;
  border-radius: 36px;
  background: var(--wechat);
}

.phone-island {
  position: absolute;
  top: 9px;
  left: 50%;
  z-index: 4;
  width: 66px;
  height: 19px;
  border-radius: 999px;
  background: #08070d;
  transform: translateX(-50%);

  i { position: absolute; top: 6px; right: 9px; width: 6px; height: 6px; border-radius: 50%; background: #171a2c; box-shadow: inset 0 0 2px rgba(101,116,255,0.8); }
}

.conversation-reveal .wechat-window {
  display: flex;
  height: 100%;
  flex-direction: column;
  border: 0;
  border-radius: 36px;
  box-shadow: none;
}

.conversation-reveal .wechat-statusbar { flex: 0 0 29px; padding: 0 18px; }
.conversation-reveal .wechat-header { flex: 0 0 54px; min-height: 54px; padding: 0 9px; }
.conversation-reveal .wechat-header strong { font-size: 13px; }

.hero-wechat-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 14px 10px 16px;
  background-image: linear-gradient(rgba(255,255,255,0.17) 1px, transparent 1px);
  background-size: 100% 36px;
}

.hero-wechat-body .chat-time { margin-bottom: 13px; font-size: 8px; }

.hero-wechat-body .message-row {
  margin-bottom: 11px;
  gap: 7px;
  animation: none;
  opacity: 0;
  transform: translateY(10px);
}

.hero-wechat-body .avatar { width: 28px; height: 28px; font-size: 11px; }
.hero-wechat-body .avatar.mine { font-size: 9px; }
.hero-wechat-body .message-bubble { max-width: 76%; min-height: 29px; padding: 7px 9px; font-size: 11px; line-height: 1.52; }
.hero-wechat-body .message-bubble::before { top: 9px; }

.stage-message-0 { opacity: var(--message-one) !important; transform: translateY(var(--message-one-y)) !important; }
.stage-message-1 { opacity: var(--message-two) !important; transform: translateY(var(--message-two-y)) !important; }
.stage-message-2 { opacity: var(--message-three) !important; transform: translateY(var(--message-three-y)) !important; }

.stage-reply {
  opacity: var(--reply-row-opacity) !important;
  transform: translateY(var(--reply-y)) !important;
}

.reply-message {
  display: grid;
  align-items: center;
  width: var(--reply-width);
  min-width: 46px;
  overflow: hidden;
  transition: width 0.16s linear;

  > span { grid-area: 1 / 1; }
}

.typing-inline {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: var(--typing-opacity);

  i { width: 5px; height: 5px; border-radius: 50%; background: #aaa; animation: typing-dot 1s ease-in-out infinite; }
  i:nth-child(2) { animation-delay: 0.14s; }
  i:nth-child(3) { animation-delay: 0.28s; }
}

.reply-copy {
  overflow: hidden;
  white-space: normal;
  opacity: var(--reply-opacity);
}

.conversation-reveal .wechat-composer { flex: 0 0 51px; min-height: 51px; padding: 7px 9px; grid-template-columns: 24px 1fr 22px 24px; gap: 6px; }
.conversation-reveal .wechat-composer > div { min-height: 34px; padding: 0 9px; font-size: 10px; }
.conversation-reveal .wechat-composer .voice-icon,
.conversation-reveal .wechat-composer .plus { width: 21px; height: 21px; }
.conversation-reveal .wechat-composer .smile { font-size: 22px; }
.conversation-reveal .demo-caption { max-width: 290px; margin: 8px 0 0 auto; }

.scroll-cue {
  position: absolute;
  bottom: 17px;
  left: 50%;
  z-index: 8;
  display: flex;
  align-items: center;
  flex-direction: column;
  color: var(--on-dark-muted) !important;
  font-size: 9px;
  letter-spacing: 0.12em;
  opacity: var(--scroll-cue-opacity);
  transform: translateX(-50%);

  > i { position: relative; display: block; width: 1px; height: 28px; margin-top: 8px; overflow: hidden; background: rgba(255,255,255,0.13); }
  > i b { position: absolute; top: -8px; left: 0; width: 1px; height: 8px; background: var(--afterimage); box-shadow: 0 0 8px rgba(101,116,255,0.56); animation: scroll-drop 1.8s ease-in-out infinite; }
}

@keyframes scroll-drop {
  0% { transform: translateY(0); opacity: 0; }
  18% { opacity: 1; }
  78% { opacity: 1; }
  100% { transform: translateY(36px); opacity: 0; }
}

#relationships,
#features,
#how,
#boundaries { scroll-margin-top: 72px; }

.relationships-section,
.how-section,
.faq-section { background: var(--paper); }

.relationships-section {
  padding: 120px 0 105px;
  background:
    radial-gradient(520px 320px at 88% 8%, rgba(255, 93, 95, 0.11), transparent 70%),
    var(--paper);
}

.section-heading,
.feature-heading {
  display: grid;
  grid-template-columns: 0.34fr 1fr;
  padding-bottom: 56px;
  gap: 46px;
}

.section-kicker {
  margin: 7px 0 0;
  color: var(--wine);
  font-size: 11px;
  letter-spacing: 0.14em;

  &.light { color: var(--on-dark); }
}

.section-heading h2,
.feature-heading h2,
.how-copy h2,
.boundary-shell h2,
.faq-heading h2,
.final-shell h2 {
  margin: 0;
  font-family: var(--font-serif);
  font-size: clamp(30px, 3.2vw, 43px);
  font-weight: 600;
  letter-spacing: -0.03em;
  line-height: 1.28;
}

.section-heading > div > p,
.feature-heading > div > p {
  max-width: 620px;
  margin: 18px 0 0;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.8;
}

.relationship-list { border-top: 1px solid var(--line); }

.relationship-item {
  display: grid;
  grid-template-columns: 84px 1fr 190px;
  align-items: center;
  min-height: 132px;
  border-bottom: 1px solid var(--line);
  gap: 30px;
  transition: padding 0.28s ease, background 0.28s ease, border-color 0.28s ease;

  > span { color: var(--wine); font-family: Georgia, serif; font-size: 12px; }
  h3 { margin: 0 0 10px; font-family: var(--font-serif); font-size: 23px; font-weight: 600; }
  p { max-width: 670px; margin: 0; color: var(--muted); font-size: 14px; line-height: 1.75; }
  small { justify-self: end; color: #6d6472; font-size: 11px; letter-spacing: 0.08em; }

  &:hover {
    padding: 0 18px;
    border-bottom-color: rgba(199,47,76,0.42);
    background: #ffe7e2;
  }
}

.relationship-disclaimer {
  max-width: 700px;
  margin: 28px 0 0 auto;
  color: var(--muted);
  font-size: 12px;
  line-height: 1.7;
  text-align: right;
}

.features-section {
  padding: 118px 0 126px;
  background: var(--afterimage-deep);
  color: var(--on-dark);
}

.feature-heading > div > p { color: #ddd7ff; }

.feature-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  border-top: 1px solid #8d82ea;
  border-left: 1px solid #8d82ea;
}

.feature-item {
  min-height: 240px;
  padding: 28px 30px 26px;
  border-right: 1px solid #8d82ea;
  border-bottom: 1px solid #8d82ea;
  background: rgba(14,13,24,0.05);
  transition: background 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;

  h3 { margin: 36px 0 14px; font-family: var(--font-serif); font-size: 23px; font-weight: 600; }
  p { margin: 0; color: #ddd7ff; font-size: 13px; line-height: 1.75; }
  > span { display: block; margin-top: 24px; color: var(--on-dark); font-size: 10px; letter-spacing: 0.12em; }

  &:hover {
    background: rgba(14,13,24,0.12);
    box-shadow: inset 3px 0 0 var(--coral-bright);
    transform: translateY(-2px);
  }
}

.feature-index { color: var(--on-dark); font-family: Georgia, serif; font-size: 11px; }

.how-section {
  padding: 125px 0;
  background: #ffddc0;
}
.step > span {
  border-color: rgba(14,143,85,0.55);
  background: var(--paper);
  color: var(--wine);
}

.how-shell {
  display: grid;
  grid-template-columns: 0.78fr 0.92fr 0.72fr;
  align-items: start;
  gap: clamp(42px, 6vw, 86px);
}

.how-copy {
  position: sticky;
  top: 112px;

  h2 { margin-top: 28px; font-size: clamp(30px, 3vw, 41px); }
  > p:not(.section-kicker) { margin: 24px 0 0; color: var(--muted); font-size: 14px; line-height: 1.85; }
}

.inline-cta {
  display: inline-flex;
  align-items: center;
  margin-top: 30px;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--wine);
  color: var(--wine) !important;
  font-size: 13px;

  svg { width: 17px; margin-left: 16px; fill: none; stroke: currentColor; stroke-width: 1.5; transition: transform 0.25s ease; }
  &:hover svg,
  &:focus-visible svg { transform: translateX(4px); }
}

.steps-list { border-top: 1px solid var(--line); }

.step {
  display: grid;
  grid-template-columns: 44px 1fr;
  min-height: 190px;
  padding: 30px 0;
  border-bottom: 1px solid var(--line);
  gap: 20px;

  > span { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border: 1px solid rgba(199,47,76,0.58); border-radius: 50%; background: var(--paper); color: var(--wine); font-family: Georgia, serif; font-size: 11px; }
  small { color: var(--muted); font-size: 10px; letter-spacing: 0.12em; }
  h3 { margin: 11px 0 13px; font-family: var(--font-serif); font-size: 22px; font-weight: 600; }
  p { margin: 0; color: var(--muted); font-size: 13px; line-height: 1.75; }
}

.character-sheet {
  margin-top: 52px;
  border: 1px solid #39344d;
  background: var(--night-soft);
  box-shadow: 14px 18px 0 rgba(101,116,255,0.12);
  color: var(--on-dark);
}

.sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 0 18px;
  border-bottom: 1px solid var(--dark-line);
  font-size: 12px;

  i { color: var(--apricot); font-size: 10px; font-style: normal; }
}

.character-sheet dl { margin: 0; padding: 4px 18px; }
.character-sheet dl > div { padding: 14px 0; border-bottom: 1px solid var(--dark-line); }
.character-sheet dl > div:last-child { border-bottom: 0; }
.character-sheet dt { margin-bottom: 6px; color: var(--on-dark-muted); font-size: 9px; letter-spacing: 0.12em; }
.character-sheet dd { margin: 0; font-size: 12px; line-height: 1.6; }
.character-sheet > p { display: flex; align-items: center; margin: 0; padding: 14px 18px; border-top: 1px solid var(--dark-line); color: var(--on-dark-muted); font-size: 10px; }
.character-sheet > p span { width: 6px; height: 6px; margin-right: 8px; border-radius: 50%; background: var(--afterimage); box-shadow: 0 0 0 3px rgba(101,116,255,0.15); }

.boundaries-section {
  padding: 116px 0;
  background: var(--wine);
  color: var(--on-dark);
}
.boundaries-section,
.faq-section,
.final-section {
  display: grid;
  align-items: center;
  min-height: calc(100svh - 72px);
}

.boundary-shell {
  display: grid;
  grid-template-columns: 1fr 0.9fr;
  gap: clamp(60px, 10vw, 150px);

  h2 { margin-top: 28px; }
}

.boundary-copy > p { margin: 38px 0 34px; color: var(--on-dark); font-size: 14px; line-height: 1.9; }
.boundary-copy ul { margin: 0; padding: 0; border-top: 1px solid rgba(255,255,255,0.22); list-style: none; }
.boundary-copy li { padding: 15px 0; border-bottom: 1px solid rgba(255,255,255,0.22); font-size: 13px; }
.boundary-copy li span { display: inline-block; width: 44px; color: rgba(255,248,243,0.82); font-family: Georgia, serif; font-size: 10px; }

.faq-section {
  padding: 118px 0 126px;
  background: var(--paper-soft);
}
.faq-shell { display: grid; grid-template-columns: 0.75fr 1.15fr; gap: clamp(60px, 10vw, 150px); }
.faq-heading h2 { margin-top: 28px; font-size: clamp(30px, 3vw, 41px); }
.faq-list { border-top: 1px solid var(--line); }
.faq-item { border-bottom: 1px solid var(--line); transition: background 0.28s ease, padding 0.28s ease; }
.faq-item:hover,
.faq-item.active { padding: 0 14px; background: #ffe7e2; }
.faq-item button { display: flex; align-items: center; justify-content: space-between; width: 100%; min-height: 80px; padding: 18px 2px; border: 0; background: transparent; color: var(--ink); cursor: pointer; text-align: left; }
.faq-item button span { padding-right: 24px; font-family: var(--font-serif); font-size: 18px; font-weight: 500; }
.faq-item button i { position: relative; flex: 0 0 auto; width: 18px; height: 18px; }
.faq-item button i::before,
.faq-item button i::after { position: absolute; top: 50%; left: 50%; width: 15px; height: 1px; background: currentColor; content: ""; transform: translate(-50%,-50%); transition: transform 0.25s ease; }
.faq-item button i::after { transform: translate(-50%,-50%) rotate(90deg); }
.faq-item.active button i::after { transform: translate(-50%,-50%) rotate(0); }
.faq-answer { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 0.35s ease; }
.faq-answer p { min-height: 0; margin: 0; overflow: hidden; color: var(--muted); font-size: 13px; line-height: 1.8; opacity: 0; transition: opacity 0.25s ease, padding 0.35s ease; }
.faq-item.active .faq-answer { grid-template-rows: 1fr; }
.faq-item.active .faq-answer p { padding: 0 44px 24px 2px; opacity: 1; }

.final-section {
  padding: 92px 0;
  background:
    radial-gradient(540px 360px at 84% 54%, rgba(255,93,95,0.22), transparent 70%),
    radial-gradient(420px 320px at 12% 84%, rgba(101,116,255,0.16), transparent 72%),
    var(--night);
  color: #f8f4ef;
}
.final-shell { display: grid; grid-template-columns: 1.15fr 0.85fr; align-items: end; gap: 70px; }
.final-shell > div:first-child > p { margin: 0 0 22px; color: var(--on-dark-muted); font-size: 10px; letter-spacing: 0.13em; }
.final-shell h2 { font-size: clamp(32px, 3.4vw, 46px); }
.final-shell > div:last-child > p { max-width: 420px; margin: 0 0 28px; color: var(--on-dark-muted); font-size: 14px; line-height: 1.8; }
.light-button {
  background: var(--coral-bright);
  box-shadow: 0 14px 34px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,248,243,0.32) inset;
  color: var(--night) !important;
}
.light-button:hover,
.light-button:focus-visible { background: #ff7774; filter: none; }

.site-footer { background: #08070d; color: var(--on-dark-muted); }
.footer-inner { display: grid; grid-template-columns: 0.6fr 1fr 1fr; align-items: center; min-height: 150px; gap: 30px; }
.footer-brand { color: #f8f4ef !important; font-family: Georgia, serif; font-size: 31px; font-weight: 700; letter-spacing: -0.06em; }
.footer-brand span { margin-left: 3px; font-family: var(--font-sans); font-size: 9px; font-weight: 500; letter-spacing: 0.08em; }
.footer-inner p { margin: 0; font-size: 11px; }
.footer-inner > div { display: flex; justify-content: flex-end; gap: 20px; font-size: 10px; }
.footer-inner a:hover,
.footer-inner a:focus-visible { color: #fff; }

.reveal {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.68s cubic-bezier(0.22,1,0.36,1) var(--delay,0ms), transform 0.68s cubic-bezier(0.22,1,0.36,1) var(--delay,0ms);

  &.visible { opacity: 1; transform: translateY(0); }
}

.home a:focus-visible,
.home button:focus-visible { outline: 2px solid currentColor; outline-offset: 4px; }

@media (max-width: 1080px) {
  .header-nav { display: none; }
  .header-actions { margin-left: auto; }
  .hero-intro { left: 40px; width: min(520px, 50vw); }
  .feature-grid { grid-template-columns: repeat(2,1fr); }
  .how-shell { grid-template-columns: 0.8fr 1fr; }
  .character-sheet { grid-column: 2; margin-top: 0; }
}

@media (max-width: 840px) {
  .page-shell,
  .header-inner { width: min(100% - 32px, 680px); }
  .user-name,
  .login,
  .logout { display: none; }
  .hero { height: auto; min-height: 0; overflow: hidden; }
  .hero-pin {
    position: relative;
    top: auto;
    height: auto;
    min-height: 0;
    padding: 72px 0 78px;
    opacity: 1;
  }
  .hero-atmosphere { display: none; }
  .memory-ribbon {
    top: 24%;
    height: 210px;
    clip-path: inset(0);
    opacity: 0.78;
    transform: translateY(-50%);
    transition: none;
  }
  .memory-ribbon i { animation-play-state: paused; }
  .hero-intro {
    position: relative;
    top: auto;
    left: auto;
    width: min(100% - 32px, 680px);
    margin: 0 auto;
    opacity: 1;
    filter: none;
    transform: none;
  }
  .hero-intro > *,
  .ready .hero-intro > * { opacity: 1; transform: none; transition: none; }
  .hero-story { position: relative; inset: auto; width: 100%; }
  .story-whisper { display: none; }
  .conversation-reveal {
    position: relative;
    top: auto;
    right: auto;
    left: auto;
    width: min(100% - 32px, 340px);
    margin: 52px auto 0;
    opacity: 1;
    filter: none;
    transform: none;
  }
  .phone-channel { justify-content: center; margin-right: 0; }
  .phone-device {
    width: min(310px, calc(100vw - 48px));
    height: auto;
    margin: 0 auto;
    opacity: 1;
    transform: none;
    transition: none;
  }
  .hero .hero-wechat-body { min-height: 0; }
  .hero-wechat-body .message-row,
  .stage-message-0,
  .stage-message-1,
  .stage-message-2,
  .stage-reply { opacity: 1 !important; transform: none !important; }
  .stage-reply .avatar { opacity: 1; }
  .reply-message { width: auto; min-width: 0; transition: none; }
  .typing-inline { display: none; }
  .reply-copy { opacity: 1; }
  .scroll-cue { display: none; }
  .section-heading,
  .feature-heading { grid-template-columns: 1fr; gap: 24px; }
  .relationship-item { grid-template-columns: 60px 1fr; padding: 28px 0; }
  .relationship-item small { grid-column: 2; justify-self: start; }
  .relationship-item:hover { padding: 28px 12px; }
  .relationship-disclaimer { text-align: left; }
  .how-shell { display: block; }
  .how-copy { position: static; max-width: 580px; }
  .steps-list { margin-top: 64px; }
  .character-sheet { max-width: 520px; margin: 64px 0 0 auto; }
  .boundary-shell,
  .faq-shell,
  .final-shell { grid-template-columns: 1fr; gap: 54px; }
  .boundary-copy > p { margin-top: 0; }
  .footer-inner { grid-template-columns: 1fr 1fr; padding: 42px 0; }
  .footer-inner > div { grid-column: 1/-1; justify-content: space-between; padding-top: 26px; border-top: 1px solid rgba(255,255,255,0.1); }
}

@media (max-width: 560px) {
  .header-inner { height: 64px; }
  .brand { font-size: 26px; }
  .header-button { min-height: 38px; padding: 0 15px; font-size: 12px; }
  .hero-pin { padding: 58px 0 68px; }
  .hero h1 { font-size: 34px; }
  .hero-lead { margin-top: 20px; font-size: 14px; line-height: 1.8; }
  .hero-actions { align-items: flex-start; flex-direction: column; margin-top: 26px; gap: 10px; }
  .primary-button { width: 100%; }
  .honest-note { align-items: flex-start; }
  .honest-note i { margin-top: 5px; }
  .conversation-reveal { width: calc(100% - 32px); }
  .hero .hero-wechat-body { min-height: 0; }
  .wechat-body { min-height: 405px; padding-right: 12px; padding-left: 12px; }
  .phone-screen .wechat-body { min-height: 0; padding-right: 10px; padding-left: 10px; }
  .message-bubble { max-width: 76%; font-size: 12px; }
  .phone-screen .message-bubble { font-size: 11px; }
  .stage-meta { font-size: 8px; }
  .relationships-section,
  .features-section,
  .how-section,
  .boundaries-section,
  .faq-section { padding: 88px 0; }
  .section-heading h2,
  .feature-heading h2,
  .how-copy h2,
  .boundary-shell h2,
  .faq-heading h2 { font-size: 34px; }
  .relationship-item { display: block; min-height: 0; }
  .relationship-item h3 { margin-top: 22px; }
  .relationship-item small { display: block; margin-top: 18px; }
  .feature-grid { grid-template-columns: 1fr; }
  .feature-item { min-height: 210px; padding: 26px 22px; }
  .step { grid-template-columns: 38px 1fr; }
  .character-sheet { margin-top: 52px; }
  .faq-shell { gap: 54px; }
  .faq-item button span { font-size: 17px; }
  .final-section { padding: 78px 0; }
  .final-shell { gap: 38px; }
  .final-shell h2 { font-size: 38px; }
  .footer-inner { display: block; }
  .footer-inner p { margin-top: 14px; }
  .footer-inner > div { display: flex; flex-direction: column; margin-top: 30px; gap: 10px; }
}

@media (prefers-reduced-motion: reduce) {
  .home *,
  .home *::before,
  .home *::after { animation-duration: 0.01ms !important; animation-iteration-count: 1 !important; transition-duration: 0.01ms !important; }
  .message-row,
  .reveal { opacity: 1; transform: none; }
}

@media (prefers-reduced-motion: reduce) and (min-width: 841px) and (min-height: 691px) {
  .hero { height: auto; min-height: 0; overflow: hidden; }
  .hero-pin { position: relative; top: auto; height: calc(100svh - 72px); min-height: 620px; opacity: 1; }
  .hero-atmosphere { display: none; }
  .memory-ribbon { clip-path: inset(0); opacity: 0.76; }
  .hero-intro {
    position: absolute;
    opacity: 1;
    filter: none;
    transform: translateY(-50%);
  }
  .hero-intro > * { opacity: 1; transform: none; }
  .hero-story { position: absolute; inset: 0; }
  .story-whisper { display: none; }
  .conversation-reveal {
    position: absolute;
    opacity: 1;
    filter: none;
    transform: translateY(-50%);
  }
  .phone-device { opacity: 1; transform: none; }
  .hero-wechat-body .message-row,
  .stage-message-0,
  .stage-message-1,
  .stage-message-2,
  .stage-reply { opacity: 1 !important; transform: none !important; }
  .reply-message { width: auto; }
  .typing-inline,
  .scroll-cue { display: none; }
  .reply-copy { opacity: 1; }
}

/* 2026-07 情绪叙事版：纸张、森林与新芽信号。首屏只承载情绪，第二屏完整展示聊天。 */
.site-header {
  border-bottom-color: rgba(215, 255, 99, 0.16);
  background: rgba(23, 40, 31, 0.96);

  &::after {
    background: var(--coral-bright);
    opacity: 0.72;
    transform: scaleX(0.16);
    transform-origin: left;
  }
}

.brand { text-shadow: none; }
.header-nav a::after { background: var(--coral-bright); }
.header-button {
  border-color: rgba(215, 255, 99, 0.56);
  box-shadow: none;

  &:hover,
  &:focus-visible { background: var(--coral-bright); color: var(--ink); }
}

.primary-button {
  background: var(--coral-bright);
  box-shadow: 0 12px 0 rgba(23, 40, 31, 0.12);
  color: var(--ink) !important;

  &:hover,
  &:focus-visible {
    filter: none;
    box-shadow: 0 16px 0 rgba(23, 40, 31, 0.15);
  }
}

.hero {
  height: calc(100svh - 72px);
  min-height: 620px;
  overflow: hidden;
  background: var(--paper);
  color: var(--ink);
}

.hero-pin {
  position: relative;
  top: auto;
  height: 100%;
  min-height: 620px;
  overflow: hidden;
  background: var(--paper);
  opacity: 1;
}

.hero-night { overflow: hidden; }
.ambient-vignette {
  background:
    radial-gradient(circle at 50% 48%, transparent 0 34%, rgba(23,40,31,0.035) 68%, rgba(23,40,31,0.11) 100%),
    linear-gradient(180deg, rgba(255,255,255,0.2), transparent 26%, transparent 80%, rgba(23,40,31,0.04));
}
.night-grain {
  background-image:
    repeating-linear-gradient(0deg, rgba(23,40,31,0.03) 0, rgba(23,40,31,0.03) 1px, transparent 1px, transparent 4px),
    repeating-linear-gradient(90deg, transparent 0, transparent 9px, rgba(23,40,31,0.018) 10px);
  opacity: 0.34;
  mix-blend-mode: multiply;
}

.memory-ribbon {
  top: 64%;
  right: -4%;
  left: -4%;
  height: clamp(82px, 14vh, 124px);
  background: var(--coral-bright);
  clip-path: inset(0 50% round 999px);
  filter: none;
  opacity: 0;
  transform: translateY(-50%) rotate(-1.2deg);

  i:nth-child(1) {
    top: 48%;
    height: 1px;
    background: rgba(23,40,31,0.22);
    filter: none;
    animation: ribbon-breathe 6s ease-in-out infinite alternate;
  }
  i:nth-child(2) {
    top: 50%;
    background: linear-gradient(90deg, transparent, rgba(23,40,31,0.25), transparent);
    box-shadow: none;
  }
  i:nth-child(3) { display: none; }
}
.ready .memory-ribbon { opacity: 0.66; }

.hero-intro {
  position: absolute;
  top: 46%;
  left: 50%;
  width: min(720px, calc(100% - 48px));
  color: var(--ink);
  text-align: center;
  filter: none;
  transform: translate3d(-50%, -50%, 0);
}
.hero-intro .eyebrow {
  justify-content: center;
  margin-bottom: 24px;
  color: var(--muted);

  span { background: var(--afterimage); box-shadow: none; }
}
.hero-intro h1 {
  max-width: none;
  color: var(--ink);
  font-size: clamp(40px, 4.4vw, 58px);
  font-weight: 600;
  letter-spacing: -0.05em;
  line-height: 1.17;
  text-shadow: none;

  em { color: var(--wine); font-style: normal; }
}
.hero-intro .hero-lead {
  max-width: 610px;
  margin: 24px auto 0;
  color: var(--muted);
  font-size: 15px;
  line-height: 1.8;
}
.hero-intro .hero-actions { justify-content: center; margin-top: 30px; }
.hero-intro .quiet-link { color: var(--ink); }
.hero-intro .quiet-link::after { background: var(--wine); }
.hero-intro .honest-note { justify-content: center; color: var(--muted); }
.hero-intro .honest-note i { border-color: var(--afterimage); box-shadow: none; }

.presence-echo {
  position: absolute;
  inset: 0;
  z-index: 3;
  overflow: hidden;
  pointer-events: none;
}
.echo-orbit {
  position: absolute;
  top: 45%;
  left: 50%;
  width: min(74vw, 860px);
  aspect-ratio: 1;
  border: 1px solid rgba(23, 182, 108, 0.2);
  border-radius: 50%;
  opacity: 0;
  transform: translate(-50%, -50%) scale(0.88);
  transition: opacity 1s ease 0.28s, transform 1.4s cubic-bezier(0.16,1,0.3,1) 0.2s;

  i,
  b { position: absolute; display: block; border-radius: 50%; }
  i:first-child { inset: 12%; border: 1px solid rgba(23,40,31,0.09); }
  i:nth-child(2) { inset: 27%; border: 1px dashed rgba(23,182,108,0.2); animation: echo-rotate 24s linear infinite; }
  b { top: 12%; left: 49%; width: 9px; height: 9px; background: var(--wine); box-shadow: 0 0 0 8px rgba(23,182,108,0.12); animation: echo-pulse 2.6s ease-in-out infinite; }
}
.ready .echo-orbit { opacity: 1; transform: translate(-50%, -50%) scale(1); }
.echo-word {
  position: absolute;
  color: rgba(23,40,31,0.32);
  font-family: Georgia, "Times New Roman", serif;
  font-size: 11px;
  letter-spacing: 0.2em;
  opacity: 0;
  transition: opacity 0.7s ease, transform 1s cubic-bezier(0.16,1,0.3,1);
}
.echo-word-one { top: 24%; left: 12%; transform: translateY(12px); }
.echo-word-two { top: 31%; right: 13%; transform: translateY(-10px); }
.echo-word-three { right: 22%; bottom: 18%; transform: translateX(-12px); }
.ready .echo-word { opacity: 1; transform: none; }
.ready .echo-word-one { transition-delay: 0.55s; }
.ready .echo-word-two { transition-delay: 0.7s; }
.ready .echo-word-three { transition-delay: 0.85s; }

@keyframes echo-rotate { to { transform: rotate(360deg); } }
@keyframes echo-pulse { 50% { transform: scale(1.55); box-shadow: 0 0 0 15px rgba(23,182,108,0.04); } }

.hero .scroll-cue {
  color: var(--muted) !important;
  opacity: 1;
}
.hero .scroll-cue > i { background: rgba(23,40,31,0.16); }
.hero .scroll-cue > i b { background: var(--wine); box-shadow: none; }

.conversation-screen {
  --screen-accent: var(--coral-bright);
  position: relative;
  display: grid;
  align-items: center;
  min-height: calc(100svh - 72px);
  overflow: hidden;
  padding: clamp(44px, 6vh, 74px) 0;
  background: var(--night);
  color: var(--on-dark);
}
.conversation-screen::before {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 48%;
  width: 1px;
  background: rgba(215,255,99,0.13);
  content: "";
}
.conversation-layout {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  align-items: center;
  gap: clamp(68px, 10vw, 148px);
}
.conversation-copy { max-width: 510px; }
.conversation-copy .story-overline { color: var(--coral-bright); }
.conversation-copy .story-overline span { background: var(--afterimage); }
.conversation-copy h2 {
  margin: 22px 0 0;
  font-size: clamp(31px, 3.1vw, 44px);
  font-weight: 600;
  letter-spacing: -0.045em;
  line-height: 1.28;
}
.conversation-copy > p:not(.story-overline) {
  margin: 20px 0 0;
  color: var(--on-dark-muted);
  font-size: 14px;
  line-height: 1.8;
}
.conversation-functions {
  max-width: 480px;
  margin: 30px 0 0;
  border-top: 1px solid var(--dark-line);
}
.conversation-functions > div {
  display: grid;
  grid-template-columns: 112px 1fr;
  align-items: center;
  min-height: 49px;
  border-bottom: 1px solid var(--dark-line);
}
.conversation-functions dt { color: var(--coral-bright); font-size: 11px; letter-spacing: 0.08em; }
.conversation-functions dd { margin: 0; color: var(--on-dark); font-size: 12px; }
.conversation-link {
  display: inline-flex;
  align-items: center;
  margin-top: 28px;
  padding-bottom: 5px;
  border-bottom: 1px solid rgba(215,255,99,0.5);
  color: var(--on-dark);
  font-size: 12px;
  gap: 14px;
}
.conversation-link span { color: var(--coral-bright); }

.conversation-screen .conversation-reveal {
  position: relative;
  top: auto;
  right: auto;
  width: 330px;
  opacity: 1;
  transform: none;
}
.conversation-screen .phone-channel { color: var(--on-dark-muted); }
.conversation-screen .phone-channel span,
.conversation-screen .demo-caption span { background: var(--coral-bright); }
.conversation-screen .phone-device {
  height: min(565px, calc(100svh - 180px));
  border-color: #395242;
  background: #0b1510;
  box-shadow: 0 34px 74px rgba(0,0,0,0.38), 18px 12px 0 rgba(215,255,99,0.11);
  opacity: 1;
  transform: none;
  transition: none;
}
.conversation-screen .phone-island i { box-shadow: inset 0 0 2px rgba(23,182,108,0.8); }
.conversation-screen .hero-wechat-body .message-row {
  opacity: 1 !important;
  transform: none !important;
  animation: none !important;
}
.conversation-screen .reply-message { width: auto; min-width: 0; transition: none; }
.conversation-screen .reply-copy { opacity: 1; }
.conversation-screen .demo-caption { color: var(--on-dark-muted); }
.conversation-screen .avatar.mine { background: #226448; }

.screen-enter {
  --screen-accent: var(--afterimage);
  position: relative;
  isolation: isolate;
}
.screen-enter::after {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 4;
  height: 3px;
  background: var(--screen-accent);
  content: "";
  pointer-events: none;
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.9s cubic-bezier(0.16,1,0.3,1);
}
.screen-enter.is-inview::after { transform: scaleX(1); }
.screen-enter .enter-left,
.screen-enter .enter-right {
  opacity: 0;
  transition: opacity 0.72s cubic-bezier(0.22,1,0.36,1), transform 0.72s cubic-bezier(0.22,1,0.36,1);
}
.screen-enter .enter-left { transform: translate3d(-34px, 12px, 0); }
.screen-enter .enter-right { transform: translate3d(42px, 14px, 0) rotate(1deg) scale(0.985); transition-delay: 0.1s; }
.screen-enter.is-inview .enter-left,
.screen-enter.is-inview .enter-right { opacity: 1; transform: none; }

.relationships-section {
  --screen-accent: #8f6748;
  background: var(--paper-soft);
}
.relationship-item:hover {
  border-bottom-color: rgba(14,143,85,0.46);
  background: rgba(215,255,99,0.58);
}
.features-section {
  --screen-accent: var(--ink);
  background: var(--coral-bright);
  color: var(--ink);
}
.features-section .section-kicker.light,
.features-section .feature-heading > div > p,
.feature-item p,
.feature-item > span,
.feature-index { color: var(--ink); }
.feature-grid { border-top-color: rgba(23,40,31,0.32); border-left-color: rgba(23,40,31,0.32); }
.feature-item {
  border-right-color: rgba(23,40,31,0.32);
  border-bottom-color: rgba(23,40,31,0.32);
  background: rgba(245,241,232,0.18);
}
.feature-item:hover { background: rgba(245,241,232,0.42); box-shadow: inset 3px 0 0 var(--wine); }

.how-section {
  --screen-accent: var(--wine);
  background: #d8cbbb;
}
.character-sheet {
  border-color: #355341;
  background: var(--night);
  box-shadow: 14px 18px 0 rgba(14,143,85,0.18);
}
.sheet-head i { color: var(--coral-bright); }
.character-sheet > p span { background: var(--coral-bright); box-shadow: 0 0 0 3px rgba(215,255,99,0.13); }

.boundaries-section {
  --screen-accent: var(--coral-bright);
  background: var(--wine-dark);
}
.faq-section {
  --screen-accent: #8f6748;
  background: var(--paper);
}
.faq-item:hover,
.faq-item.active { background: rgba(215,255,99,0.42); }

.final-section {
  --screen-accent: var(--wine);
  background: var(--coral-bright);
  color: var(--ink);
}
.final-shell > div:first-child > p,
.final-shell > div:last-child > p { color: var(--muted); }
.final-section .light-button { background: var(--ink); box-shadow: 0 14px 0 rgba(23,40,31,0.15); color: var(--on-dark) !important; }
.final-section .light-button:hover,
.final-section .light-button:focus-visible { background: var(--wine-dark); color: var(--on-dark) !important; }
.site-footer { background: var(--night); }

@media (max-width: 840px) {
  .hero { height: calc(100svh - 64px); min-height: 610px; }
  .hero-pin { height: 100%; min-height: 610px; padding: 0; }
  .hero-atmosphere { display: none; }
  .hero-intro {
    position: absolute;
    top: 45%;
    left: 50%;
    width: min(100% - 36px, 650px);
    margin: 0;
    opacity: 1;
    transform: translate3d(-50%, -50%, 0);
  }
  .hero-intro > *,
  .ready .hero-intro > * { transition: opacity 0.55s ease, transform 0.55s ease; }
  .memory-ribbon { top: 67%; height: 90px; }
  .echo-orbit { width: 640px; }
  .echo-word-one { left: 5%; }
  .echo-word-two { right: 5%; }
  .conversation-screen { min-height: 0; padding: 80px 0 88px; }
  .conversation-screen::before { display: none; }
  .conversation-layout { grid-template-columns: 1fr; gap: 52px; }
  .conversation-copy { max-width: 580px; }
  .conversation-screen .conversation-reveal { width: min(340px, 100%); margin: 0 auto; }
  .conversation-screen .phone-channel { justify-content: center; }
  .conversation-screen .phone-device { width: min(310px, calc(100vw - 48px)); height: auto; margin: 0 auto; }
  .conversation-screen .demo-caption { max-width: 300px; margin-right: auto; margin-left: auto; }
}

@media (max-width: 560px) {
  .hero { height: calc(100svh - 64px); min-height: 610px; }
  .hero-pin { min-height: 610px; }
  .hero-intro { top: 46%; }
  .hero-intro h1 { font-size: 36px; }
  .hero-intro .hero-lead { font-size: 14px; }
  .hero-intro .hero-actions { align-items: center; margin-top: 26px; }
  .hero-intro .primary-button { width: min(100%, 300px); }
  .hero-intro .honest-note { align-items: center; }
  .conversation-copy h2 { font-size: 34px; }
  .conversation-functions > div { grid-template-columns: 96px 1fr; }
  .conversation-screen .conversation-reveal { width: 100%; }
}

@media (max-height: 690px) and (min-width: 841px) {
  .hero,
  .hero-pin { height: 620px; min-height: 620px; }
  .conversation-screen { padding: 34px 0; }
  .conversation-screen .phone-device { height: calc(100svh - 170px); min-height: 400px; }
}

@media (prefers-reduced-motion: reduce) {
  .screen-enter::after { transform: scaleX(1); transition: none; }
  .screen-enter .enter-left,
  .screen-enter .enter-right,
  .screen-enter.is-inview .enter-left,
  .screen-enter.is-inview .enter-right { opacity: 1; transform: none; transition: none; }
  .echo-orbit,
  .echo-word { opacity: 1; transform: translate(-50%, -50%); transition: none; }
  .echo-word { transform: none; }
}
</style>
